import { pruneHiddenContent } from '@seen/core/observation-logic/documentUnits'
import type { DefaultError } from '@tanstack/react-query'
import { type Editor, getHTMLFromFragment } from '@tiptap/core'
import { useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { cleanEditorHtml } from '#src/components/rich-text-editor/getCleanedContent.ts'
import type { RequiredAnswersDialogProps } from '#src/components/rich-text-editor/required/RequiredAnswersDialog.tsx'
import { storedHiddenIds } from '#src/components/rich-text-editor/required/requiredGaps.ts'
import {
  holdsAnswers,
  type NoteCaptureSnapshot,
  type NoteCaptureTarget,
  snapshotNoteCaptures,
  useNoteCaptureWriter,
} from '#src/components/rich-text-editor/required/useNoteCaptureWriter.ts'
import { useRequiredAnswers } from '#src/components/rich-text-editor/required/useRequiredAnswers.ts'
import type { RequiredAnswersSurface } from '#src/hooks/requiredAnswersObservability.ts'
import { trimHTML } from '#src/utils/trimHTML.ts'

export interface PreparedNote {
  // What the note holds, to record once it exists.
  answers: NoteCaptureSnapshot | null
  // The message to store: the note as it shows, without what its conditions
  // hide, so it reads the same once submitted.
  message: string
}

export interface HeldAnswers {
  // Give the form's editor: `editorRef={held.editorRef}` and
  // `observationRecordStatus="deferred"`.
  editorRef: { current: Editor | null }
  // Render `<RequiredAnswersDialog {...held.dialog} />` beside the form.
  dialog: RequiredAnswersDialogProps
  // Before submitting `message`, the form's value: asks about unanswered
  // required questions, then takes what the note holds and the message to
  // store. `undefined` when the clinician went back to answer, or everything
  // in the note is hidden, so the submit stops.
  prepare: (message: string) => Promise<PreparedNote | undefined>
  // Once the note exists: writes what it held for its participant. A note
  // with no participant cannot record, and says so.
  record: (
    held: NoteCaptureSnapshot | null,
    target: Partial<NoteCaptureTarget> & { noteKey: string }
  ) => void
}

/* The submit steps of a single-editor form whose answers are held until it
 * is submitted (a timeline note, a comment, a reply): check required
 * questions, take what the note holds, and once the note exists write it. */
export function useHeldAnswers({
  surface,
  action = 'Submit',
}: {
  surface: RequiredAnswersSurface
  action?: 'Sign' | 'Submit'
}): HeldAnswers {
  const editorRef = useRef<Editor | null>(null)
  const getEditor = useCallback(() => editorRef.current, [])
  const required = useRequiredAnswers({ getEditor, surface, action })
  const write = useNoteCaptureWriter()
  const { check } = required

  const prepare = useCallback(
    async (message: string): Promise<PreparedNote | undefined> => {
      if ((await check()) === 'answer') {
        return undefined
      }
      const editor = editorRef.current
      if (!editor || editor.isDestroyed) {
        return { answers: null, message }
      }
      const shown = shownHtml(editor) ?? message
      if (!shown) {
        toast.error('Nothing to submit: conditions hide everything in the note')
        return undefined
      }
      return { answers: snapshotNoteCaptures(editor), message: shown }
    },
    [check]
  )

  const record = useCallback<HeldAnswers['record']>(
    (held, { noteKey, subject, encounter }) => {
      if (!held || !holdsAnswers(held)) {
        return
      }
      if (!subject) {
        toast.info(
          'Answers in the note were not recorded: it has no participant'
        )
        return
      }
      write([held], { noteKey, subject, encounter }).catch(
        (error: DefaultError) => {
          toast.error(
            `The note was saved, but its answers were not recorded${
              error instanceof Error ? `: ${error.message}` : ''
            }`
          )
        }
      )
    },
    [write]
  )

  return { editorRef, dialog: required.dialog, prepare, record }
}

// The editor's HTML without the nodes and sections its conditions hide right
// now, cleaned as the form's value is; null when it hides nothing.
export function shownHtml(editor: Editor): string | null {
  const hidden = storedHiddenIds(editor)
  if (!hidden.size) {
    return null
  }
  const pruned = editor.schema.nodeFromJSON(
    pruneHiddenContent(editor.getJSON(), hidden)
  )
  return trimHTML(
    cleanEditorHtml(getHTMLFromFragment(pruned.content, editor.schema))
  )
}
